import { useEffect, useState } from 'react';
import { EncounterCardInfo, GameState, OtherWorldContact, UserLanguage, WikiDeckResponse, gameApi } from '../api';

export const OTHER_WORLD_COLORS: Record<string, string[]> = {
  AnotherDimension: ['Green', 'Blue', 'Yellow', 'Red'], Abyss: ['Blue', 'Red'],
  CityOfTheGreatRace: ['Green', 'Yellow'], GreatHallOfCeleano: ['Blue', 'Green'], Yuggoth: ['Yellow', 'Blue'],
  PlateauOfLeng: ['Green', 'Red'], Rlyeh: ['Yellow', 'Red'], Dreamlands: ['Green', 'Blue', 'Yellow', 'Red'],
  UnknownKadath: ['Yellow', 'Red'], TheUnderworld: ['Green', 'Blue'], LostCarcosa: ['Blue', 'Yellow'], AnotherTime: ['Red', 'Green'],
};
type Entry = { promise: Promise<WikiDeckResponse>; expires: number };
const catalogs = new Map<string, Entry>();
export const clearLocationEncounterCache = () => catalogs.clear();
export function encounterFlags(state?: GameState): boolean[] {
  const settings = state?.gameSettings;
  return [true, settings?.includeDunwichCards ?? state?.includeDunwichCards ?? false, !!settings?.includeDarkPharaohCards,
    !!settings?.includeKingsportCards, !!settings?.includeFanMadeInvestigators, !!settings?.includeKingInYellowCards,
    !!settings?.includeBlackGoatCards, !!settings?.includeInsmouthCards, !!settings?.includeLurkerCards, !!settings?.includeMiskatonicCards, settings?.enableDunwichBoard ?? true, settings?.enableKingsportBoard ?? true, settings?.enableInnsmouthBoard ?? true];
}
export function useEncounterCatalog(type: 'Contact' | 'OtherWorld' | 'Exhibits' | 'CultEncounters', language: UserLanguage, state?: GameState) {
  const flags = encounterFlags(state);
  const key = JSON.stringify([type, language, ...flags]);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{ key: string; catalog?: WikiDeckResponse; error?: string }>({ key: '' });
  useEffect(() => {
    let active = true;
    let cached = catalogs.get(key);
    if (!cached || cached.expires <= Date.now()) {
      const [type, lang, ...values] = JSON.parse(key) as ['Contact' | 'OtherWorld' | 'Exhibits' | 'CultEncounters', UserLanguage, ...boolean[]];
      const promise = gameApi.getWikiDeck(type, lang, ...values as [boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean]);
      cached = { promise, expires: Date.now() + 3600000 };
      catalogs.set(key, cached);
      const entry = cached;
      promise.catch(() => { if (catalogs.get(key) === entry) catalogs.delete(key); });
    }
    cached.promise.then(catalog => { if (active) setResult({ key, catalog }); }, error => {
      if (active) setResult({ key, error: error instanceof Error ? error.message : 'Could not load encounters.' });
    });
    return () => { active = false; };
  }, [key, retry]);
  const current = result.key === key ? result : undefined;
  return { catalog: current?.catalog, loading: !current?.catalog && !current?.error, error: current?.error,
    retry: () => { setResult({ key: '' }); setRetry(value => value + 1); } };
}

export function useLocationEncounters(name: string, otherWorld: boolean, language: UserLanguage, state?: GameState) {
  const { catalog, ...status } = useEncounterCatalog(otherWorld ? 'OtherWorld' : 'Contact', language, state);
  const groups: { name: string; texts: string[] }[] = [];
  if (catalog) {
    if (otherWorld) groups.push({ name, texts: (catalog.cards as OtherWorldContact[]).filter(card => OTHER_WORLD_COLORS[name]?.includes(card.color))
      .map(card => (card.resolvers?.[name] ?? card.resolvers?.Other)?.description).filter((text): text is string => !!text) });
    else [name, ...(name === 'Silver Twilight Lodge' ? ['Inner Sanctum'] : [])].forEach(locationName => groups.push({ name: locationName,
      texts: (catalog.cards as EncounterCardInfo[]).filter(card => card.locationName === locationName).map(card => card.name) }));
  }
  return { groups, ...status };
}
